import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import TopRightAuth from "../components/TopRightAuth";
import { useEmbed } from "../context/EmbedContext";
import { Newspaper, ExternalLink, Image as ImageIcon, AlertCircle, ArrowLeft, ChevronDown } from "lucide-react";
import "./News.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const RELEVANCY_FILTERS = [
    { label: "All Articles", value: "all", levels: null },
    { label: "Critical", value: "5", levels: [5] },
    { label: "High", value: "4", levels: [4] },
    { label: "Moderate", value: "3", levels: [3] },
    { label: "Low", value: "2", levels: [2] },
];

const SORT_OPTIONS = [
    { label: "Most Relevant", value: "relevant" },
    { label: "Most Recent", value: "recent" },
    { label: "Regulatory Updates", value: "regulatory" },
    { label: "Standards Updates", value: "standards" },
    { label: "Enforcement Actions", value: "enforcement" },
];

const LEVEL_META = {
    5: { label: "Critical", className: "level-5" },
    4: { label: "High", className: "level-4" },
    3: { label: "Moderate", className: "level-3" },
    2: { label: "Low", className: "level-2" },
    1: { label: "Minimal", className: "level-1" },
};

function formatDate(isoString) {
    if (!isoString) return "";
    return new Date(isoString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function NewsCard({ article }) {
    const levelMeta = LEVEL_META[article.relevancyLevel] || LEVEL_META[1];

    const displayText = article.aiSummary
        ? article.aiSummary
        : (article.description
            ? article.description.substring(0, 200) + (article.description.length > 200 ? "..." : "")
            : "No description available.");

    const topicTags = Array.isArray(article.topicTags) ? article.topicTags : [];

    return (
        <div className="news-card fade-in-up">
            {article.urlToImage ? (
                <img
                    src={article.urlToImage}
                    alt={article.title}
                    className="news-card-image"
                    onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                    }}
                />
            ) : null}
            <div
                className="news-card-image placeholder"
                style={{ display: article.urlToImage ? "none" : "flex" }}
            >
                <ImageIcon size={48} color="var(--color-border)" aria-hidden="true" focusable="false" />
            </div>

            <div className="news-card-content">
                <div className="news-card-meta">
                    <span className={`relevancy-badge ${levelMeta.className}`}>
                        {levelMeta.label}
                    </span>
                    <span>{formatDate(article.publishedAt)}</span>
                </div>

                <h3 className="news-card-title">{article.title}</h3>

                {topicTags.length > 0 && (
                    <div className="news-topic-tags">
                        {topicTags.map((tag) => (
                            <span key={tag} className="news-topic-tag">{tag}</span>
                        ))}
                    </div>
                )}

                <p className="news-ai-summary">{displayText}</p>

                <div className="news-card-footer">
                    <span className="news-card-source">
                        {article.source?.name || "News Source"}
                    </span>
                    <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="news-card-link"
                    >
                        Read more <ExternalLink size={14} aria-hidden="true" focusable="false" />
                    </a>
                </div>
            </div>
        </div>
    );
}

function NewsSkeleton() {
    return (
        <div className="skeleton-card">
            <div className="skeleton-img"></div>
            <div className="news-card-content" style={{ padding: "24px" }}>
                <div className="skeleton-text meta"></div>
                <div className="skeleton-text title" style={{ marginTop: "16px" }}></div>
                <div className="skeleton-text title" style={{ width: "60%" }}></div>
                <div className="skeleton-text" style={{ marginTop: "16px", width: "80%" }}></div>
                <div className="skeleton-text"></div>
                <div className="skeleton-text" style={{ width: "50%" }}></div>
                <div className="skeleton-text" style={{ marginTop: "24px", width: "40%" }}></div>
            </div>
        </div>
    );
}

function News() {
    const { isEmbedMode } = useEmbed();
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState(RELEVANCY_FILTERS[0]);
    const [sortBy, setSortBy] = useState("relevant");

    const levelsParam = activeFilter.levels ? activeFilter.levels.join(",") : null;

    const { data, isLoading: loading, isError } = useQuery({
        queryKey: ["news", levelsParam, sortBy],
        queryFn: async () => {
            const params = new URLSearchParams({ sort: sortBy });
            if (levelsParam) params.set("levels", levelsParam);
            const response = await fetch(`${BACKEND_URL}/api/v1/news/?${params}`);
            if (!response.ok) throw new Error("Failed to fetch news");
            return response.json();
        },
        staleTime: 10 * 60 * 1000,
    });

    const articles = data?.articles || [];
    const lastUpdated = data?.lastUpdated || null;
    const stale = data?.stale || false;
    const error = isError ? "Unable to load latest news at this time. Please try again later." : null;

    return (
        <div className="news-page fade-in-up" style={{ animationDelay: "0.1s", position: "relative" }}>
            {!isEmbedMode && <TopRightAuth />}

            <button
                onClick={() => navigate("/")}
                className="chat-back-button fade-in-up"
                title="Back to Home"
                aria-label="Back to home"
            >
                <ArrowLeft size={16} aria-hidden="true" focusable="false" />
                <span>Back</span>
            </button>

            <div className="news-container">
                <div className="news-header">
                    <h1 className="news-title">
                        <Newspaper size={36} style={{ color: 'var(--brand-emphasis-text)' }} aria-hidden="true" focusable="false" />
                        Accessibility <span className="brand-primary-text">News</span>
                    </h1>
                    <p className="news-subtitle">
                        Curated digital accessibility news filtered by regulatory authority and relevance.
                    </p>
                    {lastUpdated && (
                        <p className="news-last-updated">
                            Last updated: {new Date(lastUpdated).toLocaleString()}{stale && " (Cached)"}
                        </p>
                    )}
                </div>

                <div className="sr-only" role="status" aria-live="polite">
                    {loading
                        ? "Loading news articles"
                        : error
                            ? error
                            : `${articles.length} ${articles.length === 1 ? "article" : "articles"} shown for ${activeFilter.label}`}
                </div>

                {error && (
                    <div className="news-error">
                        <AlertCircle size={20} aria-hidden="true" focusable="false" />
                        <span>{error}</span>
                    </div>
                )}

                {!loading && !error && (
                    <div className="news-controls">
                        <div className="news-filters">
                            {RELEVANCY_FILTERS.map((filter) => (
                                <button
                                    key={filter.value}
                                    className={`news-filter-btn ${activeFilter.value === filter.value ? "active" : ""}`}
                                    onClick={() => setActiveFilter(filter)}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>

                        <div className="news-sort-wrapper">
                            <ChevronDown size={14} className="news-sort-icon" aria-hidden="true" focusable="false" />
                            <select
                                className="news-sort-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                aria-label="Sort articles"
                            >
                                {SORT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="news-grid">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <NewsSkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    !error && (
                        <div className="news-grid">
                            {articles.length > 0 ? (
                                articles.map((article, index) => (
                                    <NewsCard key={article.url || index} article={article} />
                                ))
                            ) : (
                                <div
                                    style={{
                                        gridColumn: "1 / -1",
                                        textAlign: "center",
                                        padding: "40px",
                                        color: "var(--color-text-muted)",
                                    }}
                                >
                                    No articles found for the selected filter.
                                </div>
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

export default News;
